import { createRouter, createWebHistory } from "vue-router";
import {
  userCheckedGuard,
  loginRedirectGuard,
  permissionCheckGuard,
  NotAuthorised,
  NotFound,
} from "@switchsystems/coactive-vue-lib";
import Home from "@/views/Home.vue";

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: "active",
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }
    if (to.meta.keepPosition) {
      return null;
    }
    window.scrollTo(0, 0);
    return null;
  },
  routes: [
    {
      path: "/",
      name: "Home",
      component: Home,
      meta: {
        title: "Home",
      },
    },
    {
      path: "/rotas/on-call",
      name: "rota/OncallEdit",
      component: () => import("@/views/rota/OncallEdit.vue"),
      meta: {
        title: "Edit | Staff On-call",
        permission: {
          action: "update",
          subject: "rota.rota-oncall",
        },
      },
    },
    {
      path: "/rotas/master",
      name: "rota/RotaMasterList",
      component: () => import("@/views/rota/RotaMasterList.vue"),
      meta: {
        title: "Master Rotas",
        permission: {
          action: "update",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/rotas",
      name: "rota/RotaList",
      component: () => import("@/views/rota/RotaList.vue"),
      meta: {
        title: "Rotas",
        permission: {
          action: "read",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/rotas/create",
      name: "rota/RotaCreate",
      component: () => import("@/views/rota/RotaCreate.vue"),
      meta: {
        title: "Create | Rotas",
        permission: {
          action: "create",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/rotas/:rotaId([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/:week(\\d)?/:viewMode?",
      name: "rota/RotaView",
      component: () => import("@/views/rota/RotaView.vue"),
      props: true,
      meta: {
        title: "View | Rotas",
        keepPosition: true,
        permission: {
          action: "read",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/rotas/day",
      name: "rota/RotaDayView",
      component: () => import("@/views/rota/RotaDayView.vue"),
      props: true,
      meta: {
        title: "View Day | Rota",
        permission: {
          action: "read",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/rotas/:rotaId/day",
      name: "rota/HistoricRotaDayView",
      component: () => import("@/views/rota/DayView.vue"),
      props: true,
      meta: {
        title: "View Past Day | Rota",
        permission: {
          action: "read",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/activities/:rotaActivityId([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})",
      name: "activity/RotaActivityView",
      component: () => {
        return import("@/views/activity/RotaActivityView.vue");
      },
      props: true,
      meta: {
        title: "View | Activity",
        permission: {
          action: "read",
          subject: "rota.rota-activities",
        },
      },
    },
    {
      path: "/activities/:tab?",
      name: "activity/RotaActivityList",
      props: true,
      component: () => import("@/views/activity/RotaActivityList.vue"),
      meta: {
        title: "Activities",
        permission: {
          action: "read",
          subject: "rota.rota-activities",
        },
      },
    },
    {
      path: "/activities/day",
      name: "activity/RotaActivityDay",
      component: () => import("@/views/activity/RotaActivityDay.vue"),
      meta: {
        title: "Today | Activities",
        permission: {
          action: "read",
          subject: "rota.rota-activities",
        },
      },
    },
    {
      path: "/reports/payroll",
      name: "report/ReportPayrollView",
      component: () => import("@/views/report/ReportPayrollView.vue"),
      meta: {
        title: "Payroll | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/reports/activityreports",
      name: "report/ReportActivityReports",
      component: () => import("@/views/report/ReportActivityReportsView.vue"),
      meta: {
        title: "Activity | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/reports/client-activities/:clientId?",
      name: "report/ReportClientActivities",
      component: () => import("@/views/report/ReportClientActivities.vue"),
      props: true,
      meta: {
        title: "Client Activities | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/reports/client-behaviour/:clientId?",
      name: "report/ReportClientBehaviour",
      component: () => import("@/views/report/ReportClientBehaviour.vue"),
      props: true,
      meta: {
        title: "Client Behaviour | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/reports/declined-activities",
      name: "report/ReportDeclinedActivities",
      component: () => import("@/views/report/ReportDeclinedActivities.vue"),
      meta: {
        title: "Declined Activities | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/reports/client-experience/:clientId?",
      name: "report/ReportClientExperience",
      component: () => import("@/views/report/ReportClientExperience.vue"),
      props: true,
      meta: {
        title: "Client Experience | Report",
        permission: {
          action: "read",
          subject: "rota.rota-reporting",
        },
      },
    },
    {
      path: "/setup/staff",
      name: "setup/RotaUserList",
      component: () => import("@/views/setup/RotaUserList.vue"),
      meta: {
        title: "Staff",
        permission: {
          action: "update",
          subject: "rota.rota-users",
        },
      },
    },
    {
      path: "/setup/staff/:userId",
      name: "setup/RotaUserEdit",
      props: true,
      component: () => import("@/views/setup/RotaUserEdit.vue"),
      meta: {
        title: "View | Staff",
        permission: {
          action: "update",
          subject: "rota.rota-users",
        },
      },
    },
    {
      path: "/setup/staff/add",
      name: "setup/RotaUserAdd",
      component: () => import("@/views/setup/RotaUserAdd.vue"),
      meta: {
        title: "Add | Staff",
        permission: {
          action: "create",
          subject: "rota.rota-users",
        },
      },
    },
    {
      path: "/setup/agencies",
      name: "setup/RotaAgencyList",
      component: () => import("@/views/setup/RotaAgencyList.vue"),
      meta: {
        title: "Agencies",
        permission: {
          action: "update",
          subject: "rota.rota-agencies",
        },
      },
    },
    {
      path: "/setup/agencies/:rotaAgencyId",
      name: "setup/RotaAgencyEdit",
      props: true,
      component: () => import("@/views/setup/RotaAgencyEdit.vue"),
      meta: {
        title: "Edit | Agency",
        permission: {
          action: "update",
          subject: "rota.rota-agencies",
        },
      },
    },
    {
      path: "/setup/agencies/add",
      name: "setup/RotaAgencyAdd",
      component: () => import("@/views/setup/RotaAgencyAdd.vue"),
      meta: {
        title: "Add | Agency",
        permission: {
          action: "create",
          subject: "rota.rota-agencies",
        },
      },
    },
    {
      path: "/clients",
      name: "client/ClientList", // was setup/ClientList
      component: () => import("@/views/client/ClientList.vue"),
      meta: {
        title: "Clients",
        permission: {
          action: "read",
          subject: "clients",
        },
      },
    },
    {
      path: "/clients/:clientId/:tab?",
      name: "client/ClientView", // was setup/ClientView
      props: true,
      component: () => import("@/views/client/ClientView.vue"),
      meta: {
        title: "View | Client",
        permission: {
          action: "read",
          subject: "clients",
        },
      },
      beforeEnter: (to) => {
        if (to.query.from === "qr") {
          return {
            name: "client/QRCodeClientView",
            params: { clientId: to.params.clientId },
          };
        }
        return null;
      },
    },

    {
      path: "/clients/:clientId/qr/view",
      name: "client/QRCodeClientView",
      props: true,
      component: () => import("@/views/client/QRCodeClientView.vue"),
      meta: {
        title: "View | Client",
        permission: {
          action: "read",
          subject: "clients",
        },
      },
    },
    {
      path: "/clients/:clientId/qr/print",
      name: "client/PrintableQrCode",
      props: true,
      component: () => import("@/views/client/PrintableQrCode.vue"),
      meta: {
        title: "Print QR Code | Client",
        permission: {
          action: "read",
          subject: "clients",
        },
      },
    },
    {
      path: "/dashboard/management",
      name: "management/Dashboard",
      component: () => import("@/views/management/ManagementDashboard.vue"),
      meta: {
        title: "Management | Dashboard",
        permission: {
          action: "manage",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/dashboard/management/daily/:location?/:date?",
      name: "management/Dashboard/DailyPerformance",
      component: () => import("@/views/management/DailyPerformance.vue"),
      meta: {
        title: "Daily Performance | Dashboard",
        permission: {
          action: "manage",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/dashboard/ops/:location?",
      name: "operations/Dashboard",
      props: true,
      component: () => import("@/views/operations/DashboardView.vue"),
      meta: {
        title: "Operations | Dashboard",
        permission: {
          action: "manage",
          subject: "rota.rotas",
        },
      },
    },
    {
      path: "/:pathMatch(.*)",
      component: NotFound,
      meta: {
        title: "Page Not Found",
      },
    },
    {
      path: "/not-authorised",
      name: "not-authorised",
      component: NotAuthorised,
      meta: {
        title: "Not Authorised",
      },
    },
  ],
});

router.beforeEach(userCheckedGuard);
router.beforeEach(loginRedirectGuard);
router.beforeEach(permissionCheckGuard);

export default router;
